import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import catalogos from "./data/catalogos.json";
import "./Companies.css";

const DOCUMENT_OPTIONS = [
  { value: "NIT", label: "NIT — Identificación tributaria" },
  { value: "CC", label: "CC — Cédula de ciudadanía" },
  { value: "CE", label: "CE — Cédula de extranjería" },
  { value: "PA", label: "PA — Pasaporte" },
  { value: "PEP", label: "PEP — Permiso especial de permanencia" },
  { value: "PPT", label: "PPT — Permiso por protección temporal" },
  {
    value: "DE",
    label: "Documento de identificación extranjero",
  },
];

const countryNames = new Intl.DisplayNames(
  ["es-CO"],
  { type: "region" }
);

const COUNTRY_OPTIONS = catalogos.countries
  .map((country) => {
    const name = countryNames.of(country.code) || country.name;

    return {
      value: name,
      label: name,
      code: country.code,
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label, "es"));

const DEPARTMENT_OPTIONS = catalogos.departments.map((item) => ({
  value: item.name,
  label: item.name,
  code: item.code,
}));

function normalizeName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function findSelectedOption(options, value) {
  if (!value) return null;

  return (
    options.find(
      (option) =>
        normalizeName(option.value) === normalizeName(value)
    ) || {
      value,
      label: `${value} (valor actual)`,
    }
  );
}

function SearchableField({
  id,
  label,
  options,
  value,
  onChange,
  disabled,
}) {
  return (
    <div className="companies-select-group">
      <label htmlFor={id}>{label}</label>

      <Select
        inputId={id}
        classNamePrefix="company-select"
        options={options}
        value={findSelectedOption(options, value)}
        onChange={(option) => onChange(option?.value || "")}
        isDisabled={disabled}
        isSearchable
        isClearable
        placeholder="Selecciona..."
        noOptionsMessage={() => "Sin resultados"}
        menuPlacement="auto"
      />
    </div>
  );
}

const API_URL = (
  process.env.REACT_APP_API_URL || "/api"
).replace(/\/+$/, "");


const EMPTY_FORM = {
  company_name: "",
  nit: "",
  email: "",
  phone: "",
  plan_id: "",
  start_date: "",
  end_date: "",
  subdomain: "",

  address: "",
  city: "",
  department: "",
  country: "Colombia",
  postal_code: "",
  website: "",

  document_type: "NIT",
  tax_regime: "Responsable de IVA",
  tax_responsibility: "",
  economic_activity: "",
  legal_representative: "",
};


const OPTIONAL_FIELDS = [
  "address",
  "city",
  "department",
  "country",
  "postal_code",
  "website",
  "document_type",
  "tax_regime",
  "tax_responsibility",
  "economic_activity",
  "legal_representative",
];


const FIELD_GROUPS = [
  {
    title: "Datos de la empresa",
    fields: [
      {
        name: "company_name",
        label: "Nombre empresa",
        type: "text",
        required: true,
      },
      {
        name: "document_type",
        label: "Tipo de documento",
        type: "text",
      },
      {
        name: "nit",
        label: "Número de documento / NIT",
        type: "text",
        required: true,
      },
      {
        name: "email",
        label: "Correo",
        type: "email",
        required: true,
      },
      {
        name: "phone",
        label: "Teléfono",
        type: "tel",
        required: true,
      },
      {
        name: "legal_representative",
        label: "Representante legal",
        type: "text",
      },
    ],
  },
  {
    title: "Ubicación y contacto",
    fields: [
      { name: "address", label: "Dirección", type: "text" },
      { name: "city", label: "Ciudad", type: "text" },
      {
        name: "department",
        label: "Departamento",
        type: "text",
      },
      { name: "country", label: "País", type: "text" },
      {
        name: "postal_code",
        label: "Código postal",
        type: "text",
      },
      {
        name: "website",
        label: "Sitio web (https://...)",
        type: "url",
      },
    ],
  },
  {
    title: "Información tributaria",
    fields: [
      {
        name: "tax_regime",
        label: "Régimen tributario",
        type: "text",
      },
      {
        name: "tax_responsibility",
        label: "Responsabilidades tributarias",
        type: "text",
      },
      {
        name: "economic_activity",
        label: "Actividad económica / CIIU",
        type: "text",
      },
    ],
  },
  {
    title: "Plan y suscripción",
    fields: [
      {
        name: "plan_id",
        label: "Plan",
        type: "select",
        required: true,
      },
      {
        name: "max_users",
        label: "Máximo usuarios",
        type: "readonly",
      },
      {
        name: "start_date",
        label: "Fecha inicio",
        type: "date",
        required: true,
      },
      {
        name: "end_date",
        label: "Fecha fin",
        type: "date",
        required: true,
      },
      {
        name: "subdomain",
        label: "Subdominio",
        type: "text",
        required: true,
      },
    ],
  },
];


function getHeaders() {
  const token = localStorage.getItem("token");

  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
}


function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
}


function companyToForm(company) {
  return Object.fromEntries(
    Object.keys(EMPTY_FORM).map((key) => [
      key,
      company[key] ?? "",
    ])
  );
}


function buildPayload(data) {
  const payload = {
    company_name: data.company_name.trim(),
    nit: data.nit.trim(),
    email: data.email.trim(),
    phone: data.phone.trim(),
    plan_id: Number(data.plan_id),
    start_date: data.start_date,
    end_date: data.end_date,
    subdomain: data.subdomain.trim(),
  };

  OPTIONAL_FIELDS.forEach((field) => {
    payload[field] = (data[field] || "").trim() || null;
  });

  return payload;
}


function formatMaxUsers(value) {
  return value === null ? "Ilimitados" : value ?? "—";
}


function sortCompanies(companies) {
  return [...companies].sort((a, b) =>
    a.company_name.localeCompare(b.company_name, "es")
  );
}


function CompanyFields({
  data,
  onChange,
  prefix,
  disabled,
  plans,
  plansLoading,
  currentPlanId = null,
}) {
  const selectedPlan = plans.find(
    (plan) => String(plan.id) === String(data.plan_id)
  );

  function planLabel(plan) {
    const price = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: plan.currency,
      maximumFractionDigits: 0,
    }).format(Number(plan.monthly_price));

    return `${plan.name} — ${plan.price_from ? "Desde " : ""
      }${price}/mes${plan.is_active ? "" : " (Inactivo)"}`;
  }

  const isColombia = normalizeName(data.country) === "colombia";

  const selectedDepartment = DEPARTMENT_OPTIONS.find(
    (option) =>
      normalizeName(option.value) === normalizeName(data.department)
  );

  const cityOptions = selectedDepartment
    ? catalogos.municipalities
      .filter(
        (municipality) =>
          municipality.department_code === selectedDepartment.code
      )
      .map((municipality) => ({
        value: municipality.name,
        label: municipality.name,
        code: municipality.code,
      }))
    : [];

  function changeField(name, value) {
    onChange({
      target: { name, value },
    });
  }

  return (
    <>
      {FIELD_GROUPS.map((group) => (
        <section
          className="companies-form-section"
          key={group.title}
        >
          <h3 className="companies-section-title">
            {group.title}
          </h3>

          <div className="companies-grid">
            {group.fields.map((field) => {
              const id = `${prefix}-${field.name}`;
              if (field.name === "document_type") {
                return (
                  <SearchableField
                    key={field.name}
                    id={id}
                    label="Tipo de documento"
                    options={DOCUMENT_OPTIONS}
                    value={data.document_type}
                    onChange={(value) =>
                      changeField("document_type", value)
                    }
                    disabled={disabled}
                  />
                );
              }

              if (field.name === "country") {
                return (
                  <SearchableField
                    key={field.name}
                    id={id}
                    label="País"
                    options={COUNTRY_OPTIONS}
                    value={data.country}
                    onChange={(value) => changeField("country", value)}
                    disabled={disabled}
                  />
                );
              }

              if (field.name === "department" && isColombia) {
                return (
                  <SearchableField
                    key={field.name}
                    id={id}
                    label="Departamento / distrito"
                    options={DEPARTMENT_OPTIONS}
                    value={data.department}
                    onChange={(value) =>
                      changeField("department", value)
                    }
                    disabled={disabled}
                  />
                );
              }

              if (field.name === "city" && isColombia) {
                return (
                  <SearchableField
                    key={field.name}
                    id={id}
                    label="Ciudad / municipio"
                    options={cityOptions}
                    value={data.city}
                    onChange={(value) => changeField("city", value)}
                    disabled={disabled || !selectedDepartment}
                  />
                );
              }
              return (
                <div className="input-group" key={field.name}>
                  {field.type === "select" ? (
                    <select
                      id={id}
                      className="companies-input"
                      name={field.name}
                      value={data[field.name]}
                      onChange={onChange}
                      disabled={disabled || plansLoading}
                      required={field.required}
                    >
                      <option value="">
                        {plansLoading
                          ? "Cargando planes..."
                          : "Selecciona un plan"}
                      </option>

                      {plans
                        .filter(
                          (plan) =>
                            plan.is_active ||
                            String(plan.id) === String(currentPlanId)
                        )
                        .map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {planLabel(plan)}
                          </option>
                        ))}
                    </select>
                  ) : field.type === "readonly" ? (
                    <input
                      id={id}
                      className="companies-input"
                      type="text"
                      placeholder=" "
                      value={
                        selectedPlan
                          ? formatMaxUsers(selectedPlan.max_users)
                          : ""
                      }
                      readOnly
                      disabled={disabled}
                    />
                  ) : (
                    <input
                      id={id}
                      className="companies-input"
                      type={field.type}
                      name={field.name}
                      placeholder=" "
                      value={data[field.name] ?? ""}
                      onChange={onChange}
                      disabled={disabled}
                      min={
                        field.name === "end_date"
                          ? data.start_date || undefined
                          : undefined
                      }
                      required={Boolean(field.required)}
                    />
                  )}

                  <label htmlFor={id}>
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}


function Companies() {
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");

  const [modal, setModal] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${API_URL}/companies/`, {
        headers: getHeaders(),
      })
      .then(({ data }) => {
        if (!cancelled) setCompanies(sortCompanies(data));
      })
      .catch((error) => {
        if (!cancelled) {
          setNotice({
            type: "error",
            text: getErrorMessage(error),
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${API_URL}/plans/`, {
        headers: getHeaders(),
        params: { include_inactive: true },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPlans(data);
          setPlansError("");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setPlansError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!modal) return undefined;

    function handleEscape(event) {
      if (event.key === "Escape" && !saving) {
        setModal(null);
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [modal, saving]);

  function updateCompany(updated) {
    setCompanies((current) =>
      sortCompanies(
        current.map((company) =>
          company.id === updated.id
            ? { ...company, ...updated }
            : company
        )
      )
    );
  }

  function applyFieldChange(current, name, value) {
    const updated = {
      ...current,
      [name]: value,
    };

    if (name === "country") {
      updated.department = "";
      updated.city = "";
    }

    if (name === "department") {
      updated.city = "";
    }

    return updated;
  }

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((current) =>
      applyFieldChange(current, name, value)
    );
  }

  function handleEditChange(event) {
    const { name, value } = event.target;

    setEditData((current) =>
      applyFieldChange(current, name, value)
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (creating || plansLoading || plansError) return;

    const selectedPlan = plans.find(
      (plan) => String(plan.id) === String(formData.plan_id)
    );

    if (!selectedPlan?.is_active) {
      setNotice({
        type: "error",
        text: "Selecciona un plan activo para crear la empresa.",
      });
      return;
    }

    setCreating(true);
    setNotice(null);

    try {
      const { data } = await axios.post(
        `${API_URL}/companies/`,
        buildPayload(formData),
        { headers: getHeaders() }
      );

      setCompanies((current) =>
        sortCompanies([...current, data])
      );

      setFormData({ ...EMPTY_FORM });
      setNotice({
        type: "success",
        text: "Empresa creada correctamente.",
      });
    } catch (error) {
      setNotice({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setCreating(false);
    }
  }

  function openModal(mode, company) {
    setModalError("");
    setEditData(companyToForm(company));
    setModal({ mode, company });
  }

  function closeModal() {
    if (!saving) setModal(null);
  }

  async function handleEdit(event) {
    event.preventDefault();

    if (saving || !modal || plansLoading || plansError) return;

    const selectedPlan = plans.find(
      (plan) => String(plan.id) === String(editData.plan_id)
    );

    const keepsCurrentPlan =
      selectedPlan &&
      String(selectedPlan.id) === String(modal.company.plan_id);

    if (
      !selectedPlan ||
      (!selectedPlan.is_active && !keepsCurrentPlan)
    ) {
      setModalError(
        "Selecciona un plan activo o conserva el plan actual."
      );
      return;
    }

    setSaving(true);
    setModalError("");

    try {
      const { data } = await axios.put(
        `${API_URL}/companies/${modal.company.id}`,
        buildPayload(editData),
        { headers: getHeaders() }
      );

      updateCompany(data);
      setModal(null);
      setNotice({
        type: "success",
        text: "Empresa actualizada correctamente.",
      });
    } catch (error) {
      setModalError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus() {
    if (saving || !modal) return;

    const newActive = !modal.company.is_active;

    setSaving(true);
    setModalError("");

    try {
      const { data } = await axios.patch(
        `${API_URL}/companies/${modal.company.id}/status`,
        { is_active: newActive },
        { headers: getHeaders() }
      );

      updateCompany(data);
      setModal(null);
      setNotice({
        type: "success",
        text: newActive
          ? "Empresa desbloqueada correctamente."
          : "Empresa bloqueada correctamente.",
      });
    } catch (error) {
      setModalError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  function getCompanyPlanName(company) {
    const plan = plans.find(
      (item) => String(item.id) === String(company.plan_id)
    );

    return plan?.name || company.plan || "Sin plan";
  }

  const selectedCompany = modal?.company;
  const hasActivePlans = plans.some((plan) => plan.is_active);

  return (
    <div className="companies-container">
      <h1 className="companies-header">
        Gestión de Empresas
      </h1>

      {notice && (
        <div
          className={`companies-notice companies-notice-${notice.type}`}
          role={notice.type === "error" ? "alert" : "status"}
        >
          <span>{notice.text}</span>

          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Cerrar mensaje"
          >
            ×
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="companies-card"
      >
        <p>Los campos con * son obligatorios.</p>

        {plansError && (
          <div
            className="companies-notice companies-notice-error"
            role="alert"
          >
            No se pudieron cargar los planes: {plansError}
          </div>
        )}

        {!plansLoading && !plansError && !hasActivePlans && (
          <div
            className="companies-notice companies-notice-error"
            role="alert"
          >
            No hay planes activos disponibles para crear empresas.
          </div>
        )}

        <CompanyFields
          data={formData}
          onChange={handleChange}
          prefix="create-company"
          disabled={creating}
          plans={plans}
          plansLoading={plansLoading}
        />

        <p>
          Las URLs de acceso a NetLedger y POS se generan
          automáticamente al guardar la empresa.
        </p>

        <button
          type="submit"
          className="companies-btn"
          disabled={
            creating ||
            plansLoading ||
            Boolean(plansError) ||
            !hasActivePlans
          }
        >
          {creating ? "Creando..." : "Crear Empresa"}
        </button>
      </form>

      <hr />

      <h2 className="companies-title">
        Empresas Registradas
      </h2>

      <div className="companies-table-container">
        <table className="companies-table">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Usuarios</th>
              <th>Subdominio</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">Cargando empresas...</td>
              </tr>
            ) : companies.length === 0 ? (
              <tr>
                <td colSpan="7">
                  No hay empresas registradas.
                </td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company.id}>
                  <td className="company-name">
                    {company.company_name}
                  </td>

                  <td>{company.email || "—"}</td>

                  <td>
                    <span className={`badge ${company.plan || ""}`}>
                      {getCompanyPlanName(company)}
                    </span>
                  </td>

                  <td>{formatMaxUsers(company.max_users)}</td>
                  <td>{company.subdomain}</td>

                  <td>
                    <span
                      className={`companies-status ${company.is_active
                        ? "companies-status-active"
                        : "companies-status-blocked"
                        }`}
                    >
                      {company.is_active ? "Activa" : "Bloqueada"}
                    </span>
                  </td>

                  <td>
                    <div className="companies-actions">
                      <button
                        type="button"
                        className="company-action company-action-view"
                        onClick={() => openModal("view", company)}
                      >
                        Ver compañía
                      </button>

                      <button
                        type="button"
                        className="company-action company-action-edit"
                        onClick={() => openModal("edit", company)}
                      >
                        Editar
                      </button>

                      <button
                        type="button"
                        className={`company-action ${company.is_active
                          ? "company-action-block"
                          : "company-action-unblock"
                          }`}
                        onClick={() => openModal("status", company)}
                      >
                        {company.is_active
                          ? "Bloquear"
                          : "Desbloquear"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div
          className="companies-modal-backdrop"
          onClick={closeModal}
        >
          <section
            className="companies-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="companies-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="companies-modal-heading">
              <div>
                <small>GESTIÓN DE EMPRESAS</small>

                <h2 id="companies-modal-title">
                  {modal.mode === "view"
                    ? "Información de la compañía"
                    : modal.mode === "edit"
                      ? "Editar compañía"
                      : selectedCompany.is_active
                        ? "Bloquear compañía"
                        : "Desbloquear compañía"}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                aria-label="Cerrar ventana"
              >
                ×
              </button>
            </div>

            {modalError && (
              <div
                className="companies-notice companies-notice-error"
                role="alert"
              >
                {modalError}
              </div>
            )}

            {modal.mode === "view" && (
              <>
                <dl className="companies-details">
                  {[
                    ["Empresa", selectedCompany.company_name],
                    ["Tipo de documento", selectedCompany.document_type],
                    ["Número de documento / NIT", selectedCompany.nit],
                    ["Correo", selectedCompany.email],
                    ["Teléfono", selectedCompany.phone],
                    [
                      "Representante legal",
                      selectedCompany.legal_representative,
                    ],
                    ["Dirección", selectedCompany.address],
                    ["Ciudad", selectedCompany.city],
                    ["Departamento", selectedCompany.department],
                    ["País", selectedCompany.country],
                    ["Código postal", selectedCompany.postal_code],
                    ["Sitio web", selectedCompany.website],
                    ["Régimen tributario", selectedCompany.tax_regime],
                    [
                      "Responsabilidades tributarias",
                      selectedCompany.tax_responsibility,
                    ],
                    [
                      "Actividad económica / CIIU",
                      selectedCompany.economic_activity,
                    ],
                    ["Plan", getCompanyPlanName(selectedCompany)],
                    [
                      "Máximo usuarios",
                      formatMaxUsers(selectedCompany.max_users),
                    ],
                    ["Fecha inicio", selectedCompany.start_date],
                    ["Fecha fin", selectedCompany.end_date],
                    ["Subdominio", selectedCompany.subdomain],
                    ["URL NetLedger", selectedCompany.login_url],
                    ["URL POS", selectedCompany.login_pos_url],
                    [
                      "Estado",
                      selectedCompany.is_active
                        ? "Activa"
                        : "Bloqueada",
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>
                        {value === null ||
                          value === undefined ||
                          value === ""
                          ? "—"
                          : value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="companies-modal-footer">
                  <button
                    type="button"
                    className="company-action company-action-view"
                    onClick={closeModal}
                  >
                    Cerrar
                  </button>

                  <button
                    type="button"
                    className="companies-btn"
                    onClick={() =>
                      openModal("edit", selectedCompany)
                    }
                  >
                    Editar compañía
                  </button>
                </div>
              </>
            )}

            {modal.mode === "edit" && (
              <form onSubmit={handleEdit}>
                <p>Los campos con * son obligatorios.</p>

                {plansError && (
                  <div
                    className="companies-notice companies-notice-error"
                    role="alert"
                  >
                    No se pudieron cargar los planes: {plansError}
                  </div>
                )}

                {!selectedCompany.plan_id && (
                  <p className="companies-confirmation">
                    Esta empresa aún no tiene un plan del catálogo
                    asignado. Selecciona uno para guardar los cambios.
                  </p>
                )}

                <CompanyFields
                  data={editData}
                  onChange={handleEditChange}
                  prefix="edit-company"
                  disabled={saving}
                  plans={plans}
                  plansLoading={plansLoading}
                  currentPlanId={modal.company.plan_id}
                />

                <div className="companies-modal-footer">
                  <button
                    type="button"
                    className="company-action company-action-view"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="companies-btn"
                    disabled={
                      saving ||
                      plansLoading ||
                      Boolean(plansError) ||
                      !editData.plan_id
                    }
                  >
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </form>
            )}

            {modal.mode === "status" && (
              <>
                <p className="companies-confirmation">
                  {selectedCompany.is_active
                    ? "Al bloquear la empresa, sus usuarios perderán el acceso a las operaciones protegidas del sistema."
                    : "Al desbloquear la empresa, sus usuarios activos podrán acceder nuevamente."}
                </p>

                <strong>{selectedCompany.company_name}</strong>

                <div className="companies-modal-footer">
                  <button
                    type="button"
                    className="company-action company-action-view"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`company-action ${selectedCompany.is_active
                      ? "company-action-block"
                      : "company-action-unblock"
                      }`}
                    onClick={handleToggleStatus}
                    disabled={saving}
                  >
                    {saving
                      ? "Guardando..."
                      : selectedCompany.is_active
                        ? "Confirmar bloqueo"
                        : "Confirmar desbloqueo"}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}


export default Companies;